import classnames from 'classnames';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, ButtonVariation } from '../Button/Button';
import Checkbox from '../Checkbox';
import { ChevronDirection, ChevronIcon, FilterIcon } from '../Icon';

import './FilterDropdown.css';

/**
 * A single filter option with a display label and value.
 */
export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterDropdownProps {
  /**
   * Available filter options displayed as checkboxes in the dropdown.
   * @default []
   */
  filterOptions?: FilterOption[];

  /**
   * Currently selected filter values.
   * @default []
   */
  selectedFilters?: string[];

  /**
   * Callback fired when filters are applied or cleared.
   * Receives the new array of selected filter values.
   */
  onFiltersApply?: (selectedFilters: string[]) => void;

  /**
   * When true, only one filter can be selected at a time (radio-like behavior).
   * @default false
   */
  singleSelect?: boolean;

  /** Values that cannot be selected together. */
  exclusiveFilterValues?: string[];

  /**
   * Whether the filter button and all controls are disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional CSS class applied to the wrapper element.
   */
  className?: string;

  /**
   * Data test ID for testing.
   * @default 'filter-dropdown'
   */
  'data-testid'?: string;
}

/**
 * FilterDropdown - Multi-select checkbox filter for table columns
 *
 * Renders a "Filter" button that opens a dropdown panel containing
 * checkboxes for each available filter option. Supports both multi-select
 * and single-select modes, with Apply and Clear actions.
 * Closes on click outside or Escape key.
 */
function FilterDropdown({
  filterOptions = [],
  selectedFilters = [],
  onFiltersApply,
  singleSelect = false,
  exclusiveFilterValues = [],
  disabled = false,
  className,
  'data-testid': dataTestId,
}: FilterDropdownProps) {
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [tempSelectedFilters, setTempSelectedFilters] = useState<string[]>(selectedFilters);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const baseTestId = dataTestId ?? 'filter-dropdown';
  const dropdownId = `${baseTestId}-dropdown-panel`;
  const dropdownLabelId = `${dropdownId}-label`;
  const filterClasses = classnames('filter-search__filter', className);

  const selectedFiltersKey = selectedFilters.join(',');

  useEffect(() => {
    setTempSelectedFilters(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiltersKey]);

  useEffect(() => {
    if (!isFilterDropdownOpen || typeof document === 'undefined') {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterDropdownOpen]);

  if (filterOptions.length === 0) {
    return null;
  }

  const handleFilterToggle = () => {
    if (!disabled) {
      if (!isFilterDropdownOpen) {
        setTempSelectedFilters(selectedFilters);
      }

      setIsFilterDropdownOpen(!isFilterDropdownOpen);
    }
  };

  const handleCheckboxChange = (value: string) => {
    if (disabled) return;

    if (singleSelect) {
      setTempSelectedFilters((prev) => (prev.includes(value) ? [] : [value]));

      return;
    }

    setTempSelectedFilters((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (!exclusiveFilterValues.includes(value)) return [...prev, value];

      return [...prev.filter((v) => !exclusiveFilterValues.includes(v)), value];
    });
  };

  const closeDropdownAndRestoreFocus = () => {
    setIsFilterDropdownOpen(false);
    window.requestAnimationFrame(() => filterButtonRef.current?.focus());
  };

  const handleApplyFilters = () => {
    onFiltersApply?.(tempSelectedFilters);
    closeDropdownAndRestoreFocus();
  };

  const handleClearFilters = () => {
    setTempSelectedFilters([]);
    onFiltersApply?.([]);
    closeDropdownAndRestoreFocus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isFilterDropdownOpen) {
      closeDropdownAndRestoreFocus();
      event.preventDefault();
    }
  };

  const filterButtonClasses = classnames('filter-search__filter-button', {
    'filter-search__filter-button--open': isFilterDropdownOpen,
  });

  const filterCount = selectedFilters.length;

  return (
    <div
      ref={dropdownRef}
      className={filterClasses}
      data-testid={baseTestId}
      onKeyDown={handleKeyDown}>
      <button
        ref={filterButtonRef}
        aria-controls={dropdownId}
        aria-expanded={isFilterDropdownOpen}
        aria-haspopup='true'
        aria-label='Toggle filters'
        className={filterButtonClasses}
        data-testid={`${baseTestId}-filter-toggle`}
        disabled={disabled}
        type='button'
        onClick={handleFilterToggle}>
        <FilterIcon
          className='filter-search__filter-icon'
          size={24}
        />
        <span>Filter</span>
        {filterCount > 0 && <span className='filter-search__filter-badge'>{filterCount}</span>}
        <ChevronIcon direction={isFilterDropdownOpen ? ChevronDirection.UP : ChevronDirection.DOWN} />
      </button>

      {isFilterDropdownOpen && (
        <div
          aria-labelledby={dropdownLabelId}
          className='filter-search__dropdown'
          data-testid={`${baseTestId}-dropdown`}
          id={dropdownId}
          role='group'>
          <div
            className='filter-search__dropdown-content'
            data-testid={`${baseTestId}-dropdown-content`}>
            <p
              className='filter-search__sr-only'
              data-testid={`${baseTestId}-dropdown-label`}
              id={dropdownLabelId}>
              Filter options
            </p>
            <div
              className='filter-search__checkbox-group'
              data-testid={`${baseTestId}-filters`}>
              {filterOptions.map((option, index) => {
                const isChecked = tempSelectedFilters.includes(option.value);
                const sanitizedValue = option.value.toString().replace(/[^a-zA-Z0-9_-]/g, '-');
                const optionId = `${baseTestId}-option-${sanitizedValue || index}`;

                return (
                  <Checkbox
                    key={option.value}
                    checked={isChecked}
                    data-testid={`${baseTestId}-filter-${sanitizedValue || index}`}
                    disabled={disabled}
                    id={optionId}
                    label={option.label}
                    name='filter-options'
                    value={option.value}
                    onChange={() => handleCheckboxChange(option.value)}
                  />
                );
              })}
            </div>

            <div
              className='filter-search__button-group'
              data-testid={`${baseTestId}-actions`}>
              <Button
                className='filter-search__apply-button'
                data-testid={`${baseTestId}-apply`}
                disabled={disabled || tempSelectedFilters.length === 0}
                size='small'
                variation={ButtonVariation.SOLID}
                onClick={handleApplyFilters}>
                Apply
              </Button>
              <Button
                bordered
                className='filter-search__clear-filters-button'
                data-testid={`${baseTestId}-clear-filters`}
                disabled={disabled}
                size='small'
                onClick={handleClearFilters}>
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
